import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import {
    cancelWorkspaceQueries,
} from '../../auth/workspaceCaches';
import { useTheme } from '../../context/ThemeContext';
import GlobalSearchBar from '../../features/global-search/components/GlobalSearchBar';
import AISearchAssistant from '../../features/global-search/components/AISearchAssistant';
import { ActivityNav } from '../../features/navigation/components/ActivityNav';
import ThemeToggle from '../common/ThemeToggle';
import { BreadcrumbNav } from './BreadcrumbNav';
import {
    UserOutlined,
    SettingOutlined,
    ReloadOutlined,
    LogoutOutlined,
    RobotOutlined,
} from '@ant-design/icons';
import { useUnacknowledgedCount } from '../AnomalyAlerts';
import ShieldPulseIcon from '../icons/ShieldPulseIcon';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import type { WorkspaceDescriptor } from '../../types/workspace.types';
import './BreadcrumbNav.css';
import './TopBar.css';

export const TopBar: React.FC = () => {
    const [showUserMenu, setShowUserMenu] = useState(false);
    const navigate = useNavigate();
    const {
        user,
        logout,
        workspaces,
        workspacesLoading,
        workspaceSwitching,
        workspaceError,
        selectedWorkspaceKey,
        selectWorkspace,
    } = useAuth();
    const themeContext = useTheme();

    const theme = themeContext?.theme || 'dark';
    const { data: anomalyCount = 0 } = useUnacknowledgedCount();

    const handleLogout = useCallback(() => {
        setShowUserMenu(false);
        void logout();
    }, [logout]);

    const handleWorkspaceSelect = useCallback(async (
        workspace: WorkspaceDescriptor,
    ): Promise<void> => {
        if (workspace.key === selectedWorkspaceKey) return;

        await cancelWorkspaceQueries();
        const switched = await selectWorkspace(workspace);
        if (!switched) {
            message.error('Unable to switch workspace. Your current workspace is unchanged.');
            return;
        }

        navigate('/', { replace: true });
    }, [navigate, selectWorkspace, selectedWorkspaceKey]);

    const handleResetDashboard = useCallback(() => {
        setShowUserMenu(false);
        window.dispatchEvent(new CustomEvent('resetDashboardLayout'));
    }, []);

    const handleNavigateToProfile = useCallback(() => {
        setShowUserMenu(false);
        navigate('/profile');
    }, [navigate]);

    const handleNavigateToSettings = useCallback(() => {
        setShowUserMenu(false);
        navigate('/settings');
    }, [navigate]);

    const handleToggleUserMenu = useCallback(() => {
        setShowUserMenu(prev => !prev);
    }, []);

    // Close menu on outside click
    useEffect(() => {
        if (!showUserMenu) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('[data-user-profile]') && !target.closest('[data-dropdown-menu]')) {
                setShowUserMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showUserMenu]);

    return (
        <header className="topbar" role="banner">
            {/* Left: Breadcrumbs */}
            <div className="topbar__breadcrumbs">
                <BreadcrumbNav />
            </div>

            {/* Center: Search */}
            <div className="topbar__search">
                <GlobalSearchBar />
            </div>

            {/* Right: Actions */}
            <div className="topbar__actions">
                <div className="topbar__workspace" title={workspaceError ?? undefined}>
                    <WorkspaceSwitcher
                        options={workspaces}
                        selectedKey={selectedWorkspaceKey ?? undefined}
                        onSelect={handleWorkspaceSelect}
                        loading={workspacesLoading}
                        switching={workspaceSwitching}
                    />
                </div>

                <ActivityNav />

                {/* Anomaly Alerts */}
                <button
                    className="topbar__icon-btn topbar__alert-btn"
                    aria-label={`${anomalyCount} unacknowledged anomaly alerts`}
                    onClick={() => window.dispatchEvent(new CustomEvent('toggleAnomalyDrawer'))}
                    title="Anomaly Alerts"
                >
                    <ShieldPulseIcon size={18} />
                    {anomalyCount > 0 && (
                        <span className="topbar__alert-badge">
                            {anomalyCount > 99 ? '99+' : anomalyCount}
                        </span>
                    )}
                </button>

                {/* AI Chat Toggle */}
                <button
                    className="topbar__icon-btn"
                    aria-label="AI Assistant"
                    onClick={() => window.dispatchEvent(new CustomEvent('toggleAIChat'))}
                    title="AI Assistant"
                >
                    <RobotOutlined style={{ fontSize: 18 }} />
                </button>

                <ThemeToggle />

                {/* User Profile Pill */}
                <div style={{ position: 'relative' }} data-user-profile>
                    <button
                        className="topbar__user-pill"
                        onClick={handleToggleUserMenu}
                        type="button"
                        aria-label="User profile menu"
                        aria-expanded={showUserMenu}
                    >
                        <span className="topbar__user-name">
                            {user?.name?.split(' ')[0] || 'User'}
                        </span>
                        <div className="topbar__avatar">
                            {user?.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                    </button>

                    <div className={`topbar__dropdown ${showUserMenu ? 'topbar__dropdown--visible' : ''}`} data-dropdown-menu>
                        <div className="topbar__dropdown-user">
                            <div className="topbar__dropdown-name">
                                {user?.name || 'User'}
                            </div>
                            <div className="topbar__dropdown-email">
                                {user?.email || '—'}
                            </div>
                        </div>
                        <div className="topbar__dropdown-divider" />
                        <button className="topbar__dropdown-item" onClick={handleNavigateToProfile}>
                            <UserOutlined /><span>Profile</span>
                        </button>
                        <button className="topbar__dropdown-item" onClick={handleNavigateToSettings}>
                            <SettingOutlined /><span>Settings</span>
                        </button>
                        <div className="topbar__dropdown-divider" />
                        <button className="topbar__dropdown-item" onClick={handleResetDashboard}>
                            <ReloadOutlined /><span>Reset Dashboard Layout</span>
                        </button>
                        <div className="topbar__dropdown-divider" />
                        <button className="topbar__dropdown-item topbar__dropdown-item--danger" onClick={handleLogout}>
                            <LogoutOutlined /><span>Logout</span>
                        </button>
                    </div>
                </div>
            </div>
            <AISearchAssistant />
        </header>
    );
};

export default TopBar;
